/**
 * Overview metric rows as DashboardMetricItemData (dynamic-ready).
 * Replace getters with API calls when ready.
 */

import type { DashboardMetricItemData } from '../../../../types/dashboardMetrics.types'
import type { RangeKey } from '@dataTypes/dashboard.types'
import { getRecallKpi } from '@pages/Dashboard/RecallDetail/recallDetail.mockData'
import { getRecareKpi } from '@pages/Dashboard/RecareDetail/recareDetail.mockData'
import { getTreatmentAcceptanceKpi } from '@pages/Dashboard/TreatmentAcceptanceDetail/treatmentAcceptanceDetail.mockData'
import { parseDurationGoalMinutes } from '@utils/overviewAppointments.util'
import {
  getCaseAcceptanceConfig,
  getChairTimeConfig,
  getProductionCoreNumbers,
  getTreatmentAcceptanceOverviewExtras,
} from './dashboardOverview.mockData'

const RANGE_COMPARISON: Record<RangeKey, string> = {
  '1D': 'vs. yesterday',
  '1W': 'vs. last week',
  '1M': 'vs. last month',
  '1Y': 'vs. last year',
  YTD: 'vs. last YTD',
}

function footerFor(range: RangeKey): string {
  return `6% ($800) ${RANGE_COMPARISON[range]}`
}

// ---------------------------------------------------------------------------
// Appointments — scheduled count scales; rates stay consistent
// ---------------------------------------------------------------------------

const APPOINTMENTS_VALUES: Record<
  RangeKey,
  {
    scheduled: string
    scheduledGoal: string
    confirmRate: string
    confirmGoal: string
    openChair: string
    openChairGoal: string
    noShow: string
    noShowGoal: string
  }
> = {
  '1D': {
    scheduled: '14',
    scheduledGoal: '20',
    confirmRate: '68%',
    confirmGoal: '70%',
    openChair: '2 hrs',
    openChairGoal: '3 hrs',
    noShow: '22%',
    noShowGoal: '10%',
  },
  '1W': {
    scheduled: '110',
    scheduledGoal: '100',
    confirmRate: '70%',
    confirmGoal: '70%',
    openChair: '10 hrs',
    openChairGoal: '15 hrs',
    noShow: '19%',
    noShowGoal: '10%',
  },
  '1M': {
    scheduled: '462',
    scheduledGoal: '420',
    confirmRate: '71%',
    confirmGoal: '70%',
    openChair: '42 hrs',
    openChairGoal: '63 hrs',
    noShow: '18%',
    noShowGoal: '10%',
  },
  '1Y': {
    scheduled: '5,544',
    scheduledGoal: '5,040',
    confirmRate: '72%',
    confirmGoal: '70%',
    openChair: '504 hrs',
    openChairGoal: '756 hrs',
    noShow: '17%',
    noShowGoal: '10%',
  },
  YTD: {
    scheduled: '1,386',
    scheduledGoal: '1,260',
    confirmRate: '71%',
    confirmGoal: '70%',
    openChair: '126 hrs',
    openChairGoal: '189 hrs',
    noShow: '18%',
    noShowGoal: '10%',
  },
}

export function getOverviewAppointmentsMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = APPOINTMENTS_VALUES[range]
  return [
    {
      id: 'scheduled-today',
      leftLabel: 'Scheduled today',
      value: v.scheduled,
      valueOver: v.scheduledGoal,
      infoTooltip:
        'Number of appointments scheduled compared with the daily target.',
      showInfoIcon: true,
    },
    {
      id: 'confirmation-rate',
      leftLabel: 'Confirmation rate',
      value: v.confirmRate,
      valueOver: v.confirmGoal,
      showInfoIcon: true,
    },
    {
      id: 'open-chair-time',
      leftLabel: 'Open chair time',
      value: v.openChair,
      valueOver: v.openChairGoal,
      showInfoIcon: true,
      showWarningIcon:
        parseDurationGoalMinutes(v.openChair) >
        parseDurationGoalMinutes(v.openChairGoal),
    },
    {
      id: 'no-show',
      leftLabel: 'No-show',
      value: v.noShow,
      valueOver: v.noShowGoal,
      infoTooltip:
        'Percentage of appointments marked as no-show compared with the target.',
      showInfoIcon: true,
      showWarningIcon: true,
      valueTone:
        parseInt(v.noShow, 10) > parseInt(v.noShowGoal, 10)
          ? 'danger'
          : undefined,
    },
  ]
}

// ---------------------------------------------------------------------------
// Utilization — rates stay consistent; counts and dollar values scale
// ---------------------------------------------------------------------------

const UTILIZATION_ROW1_VALUES: Record<
  RangeKey,
  {
    providerUtil: string
    providerGoal: string
    hygieneUtil: string
    hygieneGoal: string
    sameDayTx: string
    sameDayGoal: string
    pendingTx: string
    newPatientsGoal: number
    newPatientsCount: number
  }
> = {
  '1D': {
    providerUtil: '78%',
    providerGoal: '80%',
    hygieneUtil: '63%',
    hygieneGoal: '75%',
    sameDayTx: '85%',
    sameDayGoal: '80%',
    pendingTx: '$5.6K',
    newPatientsGoal: 20,
    newPatientsCount: 27,
  },
  '1W': {
    providerUtil: '80%',
    providerGoal: '80%',
    hygieneUtil: '65%',
    hygieneGoal: '75%',
    sameDayTx: '84%',
    sameDayGoal: '80%',
    pendingTx: '$28K',
    newPatientsGoal: 100,
    newPatientsCount: 132,
  },
  '1M': {
    providerUtil: '79%',
    providerGoal: '80%',
    hygieneUtil: '67%',
    hygieneGoal: '75%',
    sameDayTx: '86%',
    sameDayGoal: '80%',
    pendingTx: '$117.6K',
    newPatientsGoal: 420,
    newPatientsCount: 554,
  },
  '1Y': {
    providerUtil: '81%',
    providerGoal: '80%',
    hygieneUtil: '68%',
    hygieneGoal: '75%',
    sameDayTx: '85%',
    sameDayGoal: '80%',
    pendingTx: '$1.41M',
    newPatientsGoal: 5040,
    newPatientsCount: 6615,
  },
  YTD: {
    providerUtil: '80%',
    providerGoal: '80%',
    hygieneUtil: '66%',
    hygieneGoal: '75%',
    sameDayTx: '85%',
    sameDayGoal: '80%',
    pendingTx: '$352.8K',
    newPatientsGoal: 1260,
    newPatientsCount: 1654,
  },
}

export function getOverviewUtilizationRow1Metrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = UTILIZATION_ROW1_VALUES[range]
  return [
    {
      id: 'provider-util',
      leftLabel: 'Provider utilization',
      value: v.providerUtil,
      valueOver: v.providerGoal,
    },
    {
      id: 'hygiene-util',
      leftLabel: 'Hygiene utilization',
      value: v.hygieneUtil,
      valueOver: v.hygieneGoal,
    },
    {
      id: 'same-day-tx',
      leftLabel: 'Same-day treatment',
      value: v.sameDayTx,
      valueOver: v.sameDayGoal,
    },
    {
      id: 'pending-tx-value',
      leftLabel: 'Pending treatment value',
      value: v.pendingTx,
    },
    {
      id: 'new-patients',
      leftLabel: 'New patients',
      rightLabel: `Goal ${v.newPatientsGoal.toLocaleString('en-US')}`,
      value: v.newPatientsCount.toLocaleString('en-US'),
    },
  ]
}

const UTILIZATION_ROW2_VALUES: Record<
  RangeKey,
  {
    diagnosedTx: string
    caseAcceptance: string
  }
> = {
  '1D': {
    diagnosedTx: '14',
    caseAcceptance: '5%',
  },
  '1W': {
    diagnosedTx: '70',
    caseAcceptance: '6%',
  },
  '1M': {
    diagnosedTx: '294',
    caseAcceptance: '5%',
  },
  '1Y': {
    diagnosedTx: '3,528',
    caseAcceptance: '6%',
  },
  YTD: {
    diagnosedTx: '882',
    caseAcceptance: '5%',
  },
}

export function getOverviewUtilizationRow2Metrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = UTILIZATION_ROW2_VALUES[range]
  return [
    {
      id: 'diagnosed-tx',
      leftLabel: 'Diagnosed treatment',
      rightLabel: 'Goal —',
      value: v.diagnosedTx,
    },
    {
      id: 'case-acceptance',
      leftLabel: 'Case acceptance',
      rightLabel: 'Goal 80%',
      value: v.caseAcceptance,
    },
  ]
}

const UTILIZATION_ROW3_VALUES: Record<
  RangeKey,
  {
    avgTime: string
    avgProviderUtil: string
    txOpportunities: string
  }
> = {
  '1D': {
    avgTime: '1 hr 30 mins',
    avgProviderUtil: '85%',
    txOpportunities: '5',
  },
  '1W': {
    avgTime: '1 hr 28 mins',
    avgProviderUtil: '86%',
    txOpportunities: '24',
  },
  '1M': {
    avgTime: '1 hr 31 mins',
    avgProviderUtil: '85%',
    txOpportunities: '104',
  },
  '1Y': {
    avgTime: '1 hr 29 mins',
    avgProviderUtil: '87%',
    txOpportunities: '1,260',
  },
  YTD: {
    avgTime: '1 hr 30 mins',
    avgProviderUtil: '86%',
    txOpportunities: '315',
  },
}

export function getOverviewUtilizationRow3Metrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = UTILIZATION_ROW3_VALUES[range]
  return [
    {
      id: 'avg-time',
      leftLabel: 'Avg. time per patient',
      rightLabel: 'Goal <2 hours',
      value: v.avgTime,
    },
    {
      id: 'avg-provider-util',
      leftLabel: 'Avg. provider utilization',
      rightLabel: 'Goal 90%',
      value: v.avgProviderUtil,
    },
    {
      id: 'tx-opportunities',
      leftLabel: 'Treatment opportunities',
      value: v.txOpportunities,
    },
  ]
}

// ---------------------------------------------------------------------------
// Recare — same KPIs as Recare detail (`getRecareKpi`)
// ---------------------------------------------------------------------------

export function getOverviewRecareTopMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const kpi = getRecareKpi(range)
  return [
    {
      id: 'patients-due',
      leftLabel: 'Patients due',
      value: kpi.patientsDue,
    },
    {
      id: 'scheduled-recare',
      leftLabel: 'Scheduled',
      value: kpi.scheduled,
    },
    {
      id: 'overdue-recare',
      leftLabel: 'Overdue',
      value: kpi.overdue,
    },
  ]
}

export function getOverviewRecareMetricsRow1(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const kpi = getRecareKpi(range)
  return [
    {
      id: 'recare-rate',
      leftLabel: 'Recare rate',
      value: kpi.recareRate,
      valueOver: '90%',
      footer: `${kpi.recareRateDelta} ${kpi.comparisonLabel}`,
      footerTrend: kpi.recareRateTrend,
    },
    {
      id: 'patients-due',
      leftLabel: 'Patients due',
      value: kpi.patientsDue,
      footer: `${kpi.patientsDueDelta} ${kpi.comparisonLabel}`,
      footerTrend: kpi.patientsDueTrend,
    },
    {
      id: 'scheduled-recare',
      leftLabel: 'Scheduled',
      value: kpi.scheduled,
      valueOver: kpi.scheduledOver,
      footer: `${kpi.scheduledDelta} ${kpi.comparisonLabel}`,
      footerTrend: kpi.scheduledTrend,
    },
  ]
}

export function getOverviewRecareMetricsRow2(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const kpi = getRecareKpi(range)
  return [
    {
      id: 'overdue-recare',
      leftLabel: 'Overdue',
      value: kpi.overdue,
      footer: `${kpi.overdueDelta} ${kpi.comparisonLabel}`,
      footerTrend: kpi.overdueTrend,
    },
    {
      id: 'unscheduled-value',
      leftLabel: 'Unscheduled value',
      value: kpi.unscheduledValue,
      footer: `${kpi.unscheduledCount} unscheduled patients`,
    },
  ]
}

// ---------------------------------------------------------------------------
// Diagnosis (Overview) — mock KPIs; replace with clinical/ledger API when ready
// ---------------------------------------------------------------------------

const DIAGNOSIS_SEEN_DIAGNOSED: Record<
  RangeKey,
  { seen: string; diagnosed: string; dxRate: string }
> = {
  '1D': { seen: '42', diagnosed: '10', dxRate: '23.4%' },
  '1W': { seen: '208', diagnosed: '49', dxRate: '23.1%' },
  '1M': { seen: '884', diagnosed: '206', dxRate: '22.8%' },
  '1Y': { seen: '10,608', diagnosed: '2,472', dxRate: '22.5%' },
  YTD: { seen: '2,652', diagnosed: '618', dxRate: '22.7%' },
}

const DIAGNOSIS_TREATMENT_AMOUNTS: Record<
  RangeKey,
  { insurance: string; patient: string }
> = {
  '1D': { insurance: '$8.4K', patient: '$7.2K' },
  '1W': { insurance: '$42K', patient: '$36K' },
  '1M': { insurance: '$168K', patient: '$144K' },
  '1Y': { insurance: '$2.0M', patient: '$1.7M' },
  YTD: { insurance: '$504K', patient: '$432K' },
}

export function getOverviewDiagnosisTopMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = DIAGNOSIS_SEEN_DIAGNOSED[range]
  const amt = DIAGNOSIS_TREATMENT_AMOUNTS[range]
  return [
    {
      id: 'diagnosis-patients-seen',
      leftLabel: 'Patients seen',
      value: v.seen,
    },
    {
      id: 'diagnosis-patients-diagnosed',
      leftLabel: 'Patients diagnosed',
      value: v.diagnosed,
    },
    {
      id: 'diagnosis-tx-insurance',
      leftLabel: 'Insurance',
      value: amt.insurance,
    },
  ]
}

export function getOverviewDiagnosisMetricsRow1(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = DIAGNOSIS_SEEN_DIAGNOSED[range]
  return [
    {
      id: 'diagnosis-patients-seen',
      leftLabel: 'Patients seen',
      value: v.seen,
      footer: footerFor(range),
      footerTrend: 'up',
    },
    {
      id: 'diagnosis-patients-diagnosed',
      leftLabel: 'Patients diagnosed',
      value: v.diagnosed,
      footer: footerFor(range),
      footerTrend: 'up',
    },
    {
      id: 'diagnosis-capture-rate',
      leftLabel: 'Diagnosis capture',
      value: v.dxRate,
      valueOver: '25%',
      footer: footerFor(range),
      footerTrend: 'up',
    },
  ]
}

export function getOverviewDiagnosisMetricsRow2(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const amt = DIAGNOSIS_TREATMENT_AMOUNTS[range]
  return [
    {
      id: 'diagnosis-tx-insurance',
      leftLabel: 'Insurance',
      value: amt.insurance,
    },
    {
      id: 'diagnosis-tx-patient',
      leftLabel: 'Patient',
      value: amt.patient,
    },
  ]
}

/** Numeric diagnosis capture % for detail charts (syncs with Overview Diagnosis card). */
export function getOverviewDiagnosisCaptureRateNumeric(
  range: RangeKey = '1D',
): number {
  return parseFloat(DIAGNOSIS_SEEN_DIAGNOSED[range].dxRate.replace('%', ''))
}

// ---------------------------------------------------------------------------
// Treatment acceptance — Overview metrics (detail page shows Same-day accepted too)
// ---------------------------------------------------------------------------

export function getOverviewTreatmentAcceptanceMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const kpi = getTreatmentAcceptanceKpi(range)
  return [
    {
      id: 'acceptance-rate',
      leftLabel: 'Acceptance rate',
      value: kpi.acceptedRate,
      valueOver: '70%',
      footer: `${kpi.acceptedRateDelta} ${kpi.comparisonLabel}`,
      footerTrend: kpi.acceptedRateTrend,
    },
    {
      id: 'presented-value',
      leftLabel: 'Presented value',
      value: kpi.presentedValue,
      footer: `${kpi.presentedDelta} ${kpi.comparisonLabel}`,
      footerTrend: kpi.presentedTrend,
    },
    {
      id: 'accepted-value',
      leftLabel: 'Accepted value',
      value: kpi.acceptedValue,
      footer: `${kpi.acceptedDelta} ${kpi.comparisonLabel}`,
      footerTrend: kpi.acceptedTrend,
    },
  ]
}

/** Overview acceptance row — top KPI tiles (Figma). */
export function getOverviewTreatmentAcceptanceTopMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const kpi = getTreatmentAcceptanceKpi(range)
  const extras = getTreatmentAcceptanceOverviewExtras(range)
  return [
    {
      id: 'tx-presented',
      leftLabel: 'Tx presented',
      value: extras.txPresented,
    },
    {
      id: 'presented-value',
      leftLabel: 'Presented value',
      value: kpi.presentedValue,
    },
    {
      id: 'accepted-value',
      leftLabel: 'Accepted value',
      value: kpi.acceptedValue,
    },
  ]
}

// ---------------------------------------------------------------------------
// Recall — same KPIs as Recall detail (`recallDetail.mockData` / getRecallKpi)
// ---------------------------------------------------------------------------

export function getOverviewRecallMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const kpi = getRecallKpi(range)
  return [
    {
      id: 'completed-recall',
      leftLabel: 'Completed recall',
      value: String(kpi.completedRecall),
      footer: `${kpi.completedDelta} ${kpi.comparisonLabel}`,
      footerTrend: kpi.completedTrend,
    },
    {
      id: 'overdue-patients-recall',
      leftLabel: 'Overdue patients',
      value: String(kpi.overduePatients),
      footer: `${kpi.overdueDelta} ${kpi.comparisonLabel}`,
      footerTrend: kpi.overdueTrend,
    },
    {
      id: 'recall-production',
      leftLabel: 'Recall production',
      value: kpi.recallProduction,
      footer: `${kpi.productionDelta} ${kpi.comparisonLabel}`,
      footerTrend: kpi.productionTrend,
    },
    {
      id: 'scheduled-recall',
      leftLabel: 'Scheduled recall',
      value: String(kpi.scheduledRecall),
    },
    {
      id: 'recall-rate',
      leftLabel: 'Recall rate',
      value: kpi.recallRate,
    },
    {
      id: 'recall-reactivation',
      leftLabel: 'Recall reactivation',
      value: kpi.recallReactivation,
    },
  ]
}

export function getOverviewRecallMetricsRow1(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const kpi = getRecallKpi(range)
  return [
    {
      id: 'completed-recall',
      leftLabel: 'Completed recall',
      value: String(kpi.completedRecall),
      footer: `${kpi.completedDelta} ${kpi.comparisonLabel}`,
      footerTrend: kpi.completedTrend,
    },
    {
      id: 'overdue-patients-recall',
      leftLabel: 'Overdue patients',
      value: String(kpi.overduePatients),
      footer: `${kpi.overdueDelta} ${kpi.comparisonLabel}`,
      footerTrend: kpi.overdueTrend,
    },
    {
      id: 'scheduled-recall',
      leftLabel: 'Scheduled recall',
      value: String(kpi.scheduledRecall),
    },
  ]
}

export function getOverviewRecallMetricsRow2(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const kpi = getRecallKpi(range)
  return [
    {
      id: 'recall-rate',
      leftLabel: 'Recall rate',
      value: kpi.recallRate,
    },
    {
      id: 'recall-reactivation',
      leftLabel: 'Recall reactivation',
      value: kpi.recallReactivation,
    },
    {
      id: 'recall-production',
      leftLabel: 'Recall production',
      value: kpi.recallProduction,
      footer: `${kpi.productionDelta} ${kpi.comparisonLabel}`,
      footerTrend: kpi.productionTrend,
    },
  ]
}

// ---------------------------------------------------------------------------
// No-show rate — counts scale; rate stays consistent
// ---------------------------------------------------------------------------

const NO_SHOW_VALUES: Record<
  RangeKey,
  { lostChairTime: string; scheduledToday: string; noShow: string }
> = {
  '1D': { lostChairTime: '4.5 hrs', scheduledToday: '87%', noShow: '3' },
  '1W': {
    lostChairTime: '22.5 hrs',
    scheduledToday: '85%',
    noShow: '15',
  },
  '1M': {
    lostChairTime: '94.5 hrs',
    scheduledToday: '86%',
    noShow: '63',
  },
  '1Y': {
    lostChairTime: '1,134 hrs',
    scheduledToday: '87%',
    noShow: '756',
  },
  YTD: {
    lostChairTime: '283.5 hrs',
    scheduledToday: '86%',
    noShow: '189',
  },
}

export function getOverviewNoShowMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = NO_SHOW_VALUES[range]
  return [
    {
      id: 'ns-lost-chair',
      leftLabel: 'Lost chair time',
      value: v.lostChairTime,
    },
    {
      id: 'ns-scheduled',
      leftLabel: 'Scheduled',
      value: v.scheduledToday,
    },
    { id: 'ns-no-show', leftLabel: 'No-show', value: v.noShow },
  ]
}

/** Front Desk Overview row: open chair + row-3 timing/util + pending treatment value (mock). */
export function getOverviewFrontDeskMetricsRow(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const openChair = NO_SHOW_VALUES[range].lostChairTime
  const r3 = UTILIZATION_ROW3_VALUES[range]
  const r1 = UTILIZATION_ROW1_VALUES[range]
  return [
    {
      id: 'fd-open-chair',
      leftLabel: 'Open chair time',
      value: openChair,
    },
    {
      id: 'fd-avg-time',
      leftLabel: 'Avg. time per patient',
      rightLabel: 'Goal <2 hours',
      value: r3.avgTime,
    },
    {
      id: 'fd-avg-provider-util',
      leftLabel: 'Avg. provider utilization',
      rightLabel: 'Goal 90%',
      value: r3.avgProviderUtil,
    },
    {
      id: 'fd-pending-tx',
      leftLabel: 'Pending treatment value',
      value: r1.pendingTx,
    },
  ]
}

// ---------------------------------------------------------------------------
// Same-day cancellation — counts scale; rate stays consistent
// ---------------------------------------------------------------------------

const SAME_DAY_CANCEL_VALUES: Record<
  RangeKey,
  { totalAppts: string; cancellations: string; openChair: string }
> = {
  '1D': { totalAppts: '60', cancellations: '4', openChair: '3 hrs' },
  '1W': { totalAppts: '300', cancellations: '18', openChair: '15 hrs' },
  '1M': { totalAppts: '1,260', cancellations: '76', openChair: '63 hrs' },
  '1Y': { totalAppts: '15,120', cancellations: '907', openChair: '756 hrs' },
  YTD: { totalAppts: '3,780', cancellations: '227', openChair: '189 hrs' },
}

export function getOverviewSameDayCancellationMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = SAME_DAY_CANCEL_VALUES[range]
  return [
    { id: 'sdc-total', leftLabel: 'Total appointments', value: v.totalAppts },
    {
      id: 'sdc-cancelled',
      leftLabel: 'Same-day cancellation',
      value: v.cancellations,
    },
    { id: 'sdc-open-chair', leftLabel: 'Open chair time', value: v.openChair },
  ]
}

// ---------------------------------------------------------------------------
// Appointment overrun (Overview line-charts row) — mock until API
// ---------------------------------------------------------------------------

/** Canonical overrun counts — detail chart/tables scale to these totals. */
export const APPOINTMENT_OVERRUN_OVERRUNS: Record<RangeKey, number> = {
  '1D': 60,
  '1W': 302,
  '1M': 1284,
  '1Y': 15408,
  YTD: 3852,
}

/** Matches Appointment overrun detail KPI overrun rates. */
const APPOINTMENT_OVERRUN_RATE_PCT: Record<RangeKey, number> = {
  '1D': 14.2,
  '1W': 13.6,
  '1M': 13.1,
  '1Y': 12.8,
  YTD: 12.9,
}

const APPOINTMENT_OVERRUN_AVG_DELAY: Record<RangeKey, string> = {
  '1D': '30 mins',
  '1W': '28 mins',
  '1M': '26 mins',
  '1Y': '24 mins',
  YTD: '25 mins',
}

function formatOverrunMetricCount(n: number): string {
  return n.toLocaleString('en-US')
}

function completedAppointmentsFromOverrun(
  overruns: number,
  overrunRatePct: number,
): number {
  return Math.round(overruns / (overrunRatePct / 100))
}

export function getAppointmentOverrunOverviewTotals(range: RangeKey = '1D'): {
  overruns: number
  completed: number
} {
  const overruns = APPOINTMENT_OVERRUN_OVERRUNS[range]
  return {
    overruns,
    completed: completedAppointmentsFromOverrun(
      overruns,
      APPOINTMENT_OVERRUN_RATE_PCT[range],
    ),
  }
}

const APPOINTMENT_OVERRUN_VALUES: Record<
  RangeKey,
  { overrun: string; completed: string; avgDelay: string }
> = (Object.keys(APPOINTMENT_OVERRUN_OVERRUNS) as RangeKey[]).reduce(
  (acc, range) => {
    const { overruns, completed } = getAppointmentOverrunOverviewTotals(range)
    acc[range] = {
      overrun: formatOverrunMetricCount(overruns),
      completed: formatOverrunMetricCount(completed),
      avgDelay: APPOINTMENT_OVERRUN_AVG_DELAY[range],
    }
    return acc
  },
  {} as Record<
    RangeKey,
    { overrun: string; completed: string; avgDelay: string }
  >,
)

/** Providers used in Appointment overrun detail by-provider table (mock). */
export const APPOINTMENT_OVERRUN_PROVIDER_NAMES: string[] = [
  'Andrew K. Smith',
  'Jessica L. Johnson',
  'Michael T. Brown',
  'Emily R. Davis',
  'David J. Wilson',
  'Sarah M. Garcia',
  'Chris A. Martinez',
  'Jordan P. Lee',
  'Samantha R. Taylor',
  'Alex K. Anderson',
]

export function getOverviewAppointmentOverrunMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = APPOINTMENT_OVERRUN_VALUES[range]
  return [
    {
      id: 'overrun-appts',
      leftLabel: 'Overrun appointments',
      value: v.overrun,
    },
    {
      id: 'overrun-completed',
      leftLabel: 'Completed appointments',
      value: v.completed,
    },
    {
      id: 'overrun-avg-delay',
      leftLabel: 'Avg. delay',
      value: v.avgDelay,
    },
  ]
}

export function getOverviewChairTimeMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const c = getChairTimeConfig(range)
  return [
    {
      id: 'chair-total',
      leftLabel: 'Total chair time',
      value: c.totalHrs,
    },
    {
      id: 'chair-scheduled',
      leftLabel: 'Scheduled time',
      value: c.schedHrs,
    },
    {
      id: 'chair-open',
      leftLabel: 'Open chair time',
      value: c.openHrs,
    },
  ]
}

export function getOverviewCaseAcceptanceMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const c = getCaseAcceptanceConfig(range)
  return [
    {
      id: 'case-accepted-value',
      leftLabel: 'Accepted value',
      value: c.acceptedVal,
    },
    {
      id: 'case-same-day-value',
      leftLabel: 'Same-day value',
      value: c.sameDayVal,
    },
    {
      id: 'case-pending-value',
      leftLabel: 'Pending value',
      value: c.pendingVal,
    },
  ]
}

/** Owner Overview KPI row — revenue + selected utilization metrics. */
export function getOwnerOverviewKpiMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const c = getProductionCoreNumbers(range)
  const row2 = getOverviewUtilizationRow2Metrics(range)
  const row3 = getOverviewUtilizationRow3Metrics(range)
  const caseM = row2.find((m) => m.id === 'case-acceptance')
  const avgProv = row3.find((m) => m.id === 'avg-provider-util')
  const txOpp = row3.find((m) => m.id === 'tx-opportunities')
  const metrics: DashboardMetricItemData[] = [
    {
      id: 'owner-revenue-per-visit',
      leftLabel: 'Revenue per visit',
      value: `$${c.avgAppt}`,
    },
  ]
  if (caseM) {
    metrics.push({
      ...caseM,
      id: 'owner-case-acceptance-rate',
      leftLabel: 'Case acceptance rate',
    })
  }
  if (avgProv) {
    metrics.push({
      ...avgProv,
      id: 'owner-avg-provider-util',
    })
  }
  if (txOpp) {
    metrics.push({
      ...txOpp,
      id: 'owner-tx-opportunities',
    })
  }
  return metrics
}
